import React, { useEffect, useMemo, useState } from 'react';
import { detachAlias, fetchDriverDetail, renameDriver } from '../auth/api';
import { useAuth } from '../auth/AuthContext';
import { formatLapSeconds } from '../analytics/lapTime';
import { formatPaceIndex } from '../analytics/ranking';
import { DriverDetail } from '../types';
import { Sparkline } from './charts/Sparkline';
import { consistencyLabel } from './EventStats';
import { navigate } from './EventsList';
import { SessionBadge } from './SessionBadge';
import './EventsList.css';
import './EventStats.css';
import './RankingsPage.css';

function formatSeconds(seconds: number | null): string {
  return seconds === null ? '—' : formatLapSeconds(seconds);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

function signed(n: number): string {
  const r = Math.round(n);
  return r > 0 ? `+${r}` : String(r);
}

interface DriverDetailPageProps {
  driverId: string;
}

/** One driver: the names they have raced under, and every ranked event. */
export const DriverDetailPage: React.FC<DriverDetailPageProps> = ({ driverId }) => {
  const { isAdmin } = useAuth();
  const [driver, setDriver] = useState<DriverDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDriverDetail(driverId)
      .then((d) => {
        if (!cancelled) setDriver(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load driver');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [driverId]);

  const ratingTrace = useMemo(() => {
    if (!driver || driver.events.length === 0) return [];
    return [driver.events[0].ratingBefore, ...driver.events.map((e) => e.ratingAfter)];
  }, [driver]);

  const totals = useMemo(() => {
    const events = driver?.events ?? [];
    return {
      laps: events.reduce((s, e) => s + e.lapCount, 0),
      tracks: new Set(events.map((e) => e.trackName)).size,
    };
  }, [driver]);

  const saveName = async (): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      setDriver(await renameDriver(driverId, draft));
      setEditing(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not rename');
    } finally {
      setBusy(false);
    }
  };

  const split = async (aliasId: string): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      const created = await detachAlias(aliasId);
      navigate(`/rankings/${created.id}`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not split the name off');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App">
      <div className="app-header">
        <h1>Karting Live Timing</h1>
        <SessionBadge />
      </div>
      <div className="app-main">
        <div className="main-content">
          <div className="events-page rk-page">
            <div className="events-toolbar">
              <button className="events-link-button" onClick={() => navigate('/rankings')}>
                ← Back to rankings
              </button>
              {isAdmin && driver && !editing && (
                <button
                  className="events-link-button"
                  onClick={() => {
                    setDraft(driver.displayName);
                    setEditing(true);
                  }}
                >
                  ✎ Rename
                </button>
              )}
            </div>

            {actionError && <div className="events-error">Error: {actionError}</div>}

            {editing && (
              <div className="events-edit-form">
                <label>
                  <span>Display name</span>
                  <input value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
                </label>
                <div className="events-confirm-actions">
                  <button
                    className="events-link-button"
                    onClick={() => void saveName()}
                    disabled={busy}
                  >
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    className="events-link-button"
                    onClick={() => setEditing(false)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {loading && <div className="events-loading">Loading driver…</div>}
            {error && <div className="events-error">Error: {error}</div>}

            {!loading && !error && driver && (
              <>
                <div className="event-meta">
                  <h2 className="event-meta-title">{driver.displayName}</h2>
                  <div className="event-meta-grid">
                    <div>
                      <span className="event-meta-label">Rating</span>
                      <span className="event-meta-value rk-rating">
                        {driver.ratedEvents > 0 ? Math.round(driver.rating) : 'Unrated'}
                        {ratingTrace.length > 2 && (
                          <Sparkline
                            values={ratingTrace}
                            width={120}
                            height={28}
                            title="Rating after each event"
                          />
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="event-meta-label">Rated events</span>
                      <span className="event-meta-value">{driver.ratedEvents}</span>
                    </div>
                    <div>
                      <span className="event-meta-label">Events</span>
                      <span className="event-meta-value">{driver.events.length}</span>
                    </div>
                    <div>
                      <span className="event-meta-label">Laps</span>
                      <span className="event-meta-value">{totals.laps.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="event-meta-label">Tracks</span>
                      <span className="event-meta-value">{totals.tracks}</span>
                    </div>
                  </div>
                </div>

                <h3 className="events-section-title">
                  Names in the timing feed
                  <span className="es-sub">
                    {' '}
                    · laps under any of these count for this driver
                  </span>
                </h3>
                <ul className="rk-aliases">
                  {driver.aliases.map((a) => (
                    <li key={a.id}>
                      <span>{a.label}</span>
                      {isAdmin && driver.aliases.length > 1 && (
                        <button
                          className="events-link-button"
                          onClick={() => void split(a.id)}
                          disabled={busy}
                          title="This name is a different person: give it its own driver"
                        >
                          Split off
                        </button>
                      )}
                    </li>
                  ))}
                </ul>

                <h3 className="events-section-title">Events</h3>
                {driver.events.length === 0 ? (
                  <div className="events-empty">No ranked events.</div>
                ) : (
                  <div className="events-table-wrapper">
                    <table className="events-table rk-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Event</th>
                          <th>Track</th>
                          <th className="num">Laps</th>
                          <th className="num">Clean</th>
                          <th className="num">Best</th>
                          <th className="num">Clean avg</th>
                          <th className="num">Pace</th>
                          <th>Consistency</th>
                          <th className="num">Rating</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...driver.events].reverse().map((e) => {
                          const c = consistencyLabel(e.consistencyCv);
                          const delta = e.ratingAfter - e.ratingBefore;
                          return (
                            <tr
                              key={e.eventId}
                              className="events-row-clickable"
                              onClick={() => navigate(`/events/${e.eventId}`)}
                            >
                              <td>{formatDate(e.startedAt)}</td>
                              <td className="events-title-cell">{e.title}</td>
                              <td>{e.trackName}</td>
                              <td className="num">{e.lapCount}</td>
                              <td className="num">{e.cleanLapCount}</td>
                              <td className="num">{formatSeconds(e.bestSeconds)}</td>
                              <td className="num">{formatSeconds(e.cleanAverageSeconds)}</td>
                              <td className="num">{formatPaceIndex(e.paceIndex)}</td>
                              <td className={c.cls}>{c.text}</td>
                              <td className="num">
                                {e.qualified ? (
                                  <>
                                    {Math.round(e.ratingAfter)}{' '}
                                    <span
                                      className={`rk-delta${
                                        delta > 0.5 ? ' rk-delta--up' : delta < -0.5 ? ' rk-delta--down' : ''
                                      }`}
                                    >
                                      {signed(delta)}
                                    </span>
                                  </>
                                ) : (
                                  <span
                                    className="rk-unqualified"
                                    title="Too few clean laps to be compared with the field"
                                  >
                                    not rated
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverDetailPage;
